import { useQuery } from "@tanstack/react-query";
import { api } from "./api";

export interface Catalog {
  units: Array<{ id: string; name: string; kind: string; active: boolean; areaIds: string[]; legalEntityName: string | null; legalDocument: string | null }>;
  areas: Array<{ id: string; key: string; name: string; active: boolean; subjects: Array<{ id: string; name: string; active: boolean }> }>;
  stages: Array<{ id: string; scope: "processo" | "caso"; key: string; name: string; position: number; color: string; isFinal: boolean; areaId: string | null; active: boolean }>;
  activityTypes: Array<{ id: string; key: string; name: string; requiresProcessReview: boolean; requiresEvidence: boolean; defaultRisk: string; active: boolean; points?: number; defaultChecklist?: string[]; checklistRequired?: boolean }>;
  roles: Array<{ id: string; key: string; name: string; system: boolean; actions: string[] }>;
  users: Array<{ id: string; displayName: string; unitId: string; email: string; avatarUpdatedAt?: string | null; supervisorUserId?: string | null }>;
  allActions: string[];
  namedPermissions: Array<{ key: string; label: string }>;
  ruleEvents: Array<{ type: string; label: string; fields: string[] }>;
  me: { id: string; unitId: string; displayName: string; actions: string[] };
}

export function useCatalog() {
  return useQuery({ queryKey: ["catalogo"], queryFn: () => api.get<Catalog>("/config/catalogo"), staleTime: 60_000 });
}

/** Helpers derivados do catálogo (tolerantes a carregamento). */
export function useLookups() {
  const { data } = useCatalog();
  const users = new Map((data?.users ?? []).map((u) => [u.id, u.displayName]));
  const areas = new Map((data?.areas ?? []).map((a) => [a.id, a.name]));
  const units = new Map((data?.units ?? []).map((u) => [u.id, u.name]));
  const types = new Map((data?.activityTypes ?? []).map((t) => [t.id, t]));
  const stages = new Map((data?.stages ?? []).map((s) => [s.id, s]));
  const actionTypes = new Map((data?.areas ?? []).flatMap((a) => a.subjects.map((s) => [s.id, s.name] as const)));
  const actions = new Set(data?.me.actions ?? []);
  return {
    catalog: data,
    userName: (id?: string | null) => (id ? users.get(id) ?? "—" : "—"),
    areaName: (id?: string | null) => (id ? areas.get(id) ?? "—" : "—"),
    unitName: (id?: string | null) => (id ? units.get(id) ?? "—" : "—"),
    typeOf: (id?: string | null) => (id ? types.get(id) : undefined),
    stageOf: (id?: string | null) => (id ? stages.get(id) : undefined),
    actionTypeName: (id?: string | null) => (id ? actionTypes.get(id) ?? "—" : "—"),
    /** Tem a ação em algum escopo (o servidor decide o escopo exato). */
    can: (action: string) => actions.has(action),
    me: data?.me,
  };
}
